import { useEffect, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { listMaBueroAssignees, type MaBueroAssignee } from "@/lib/material";

export function MaBearbeitungTransferDialog({
  open,
  mode,
  maNummer,
  loading,
  currentAssigneeName,
  onClose,
  onConfirmRelease,
  onConfirmAssign,
}: {
  open: boolean;
  mode: "release" | "assign";
  maNummer: string;
  loading: boolean;
  currentAssigneeName?: string | null;
  onClose: () => void;
  onConfirmRelease: (grund: string) => void;
  onConfirmAssign: (assigneeLogin: string, grund: string) => void;
}) {
  const [grund, setGrund] = useState("");
  const [assigneeLogin, setAssigneeLogin] = useState("");
  const [assignees, setAssignees] = useState<MaBueroAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGrund("");
    setAssigneeLogin("");
  }, [open, mode]);

  useEffect(() => {
    if (!open || mode !== "assign") {
      setAssignees([]);
      return;
    }
    let cancelled = false;
    setAssigneesLoading(true);
    void (async () => {
      try {
        const list = await listMaBueroAssignees();
        if (!cancelled) setAssignees(list);
      } catch {
        if (!cancelled) setAssignees([]);
      } finally {
        if (!cancelled) setAssigneesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode]);

  const trimmedGrund = grund.trim();
  const canSubmit =
    trimmedGrund.length > 0 && (mode === "release" || assigneeLogin.trim().length > 0);

  const title = mode === "release" ? "Bearbeitung freigeben" : "Bearbeitung zuweisen";

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={trimmedGrund.length > 0 || assigneeLogin.trim().length > 0}
      size="sm"
      zIndexClass="z-[130]"
      title={title}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} />
          <Button
            type="button"
            variant={mode === "release" ? "secondary" : "primary"}
            disabled={!canSubmit}
            loading={loading}
            onClick={() => {
              if (mode === "release") {
                onConfirmRelease(trimmedGrund);
                return;
              }
              onConfirmAssign(assigneeLogin.trim(), trimmedGrund);
            }}
          >
            {mode === "release" ? "Freigeben" : "Zuweisen"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-label-secondary">
          Materialanforderung{" "}
          <span className="font-semibold text-label">{maNummer}</span>
          {mode === "release" ? (
            <>
              {" "}
              freigeben? Die Anforderung kann danach wieder von einem anderen Büro-Mitarbeiter
              übernommen werden.
            </>
          ) : (
            <> an einen Kollegen im Büro zuweisen.</>
          )}
        </p>

        {currentAssigneeName && mode === "release" ? (
          <p className="text-sm text-label-secondary">
            Aktuell bearbeitet von{" "}
            <span className="font-medium text-label">{currentAssigneeName}</span>.
          </p>
        ) : null}

        {mode === "assign" ? (
          <div className="space-y-1">
            <label className="eh-label" htmlFor="ma-assignee">
              Zielperson<span className="ml-1 text-status-red">*</span>
            </label>
            {assigneesLoading ? (
              <div className="flex items-center gap-2 text-sm text-label-secondary">
                <Spinner />
                Büro-Mitarbeiter werden geladen …
              </div>
            ) : (
              <select
                id="ma-assignee"
                className="eh-input w-full"
                value={assigneeLogin}
                disabled={loading || assignees.length === 0}
                onChange={(e) => setAssigneeLogin(e.target.value)}
              >
                <option value="">Bitte wählen …</option>
                {assignees.map((member) => (
                  <option key={member.user_login} value={member.user_login}>
                    {member.display_name} ({member.user_login})
                  </option>
                ))}
              </select>
            )}
          </div>
        ) : null}

        <div className="space-y-1">
          <label className="eh-label" htmlFor="ma-transfer-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="ma-transfer-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Kurz dokumentieren, warum …"
            value={grund}
            disabled={loading}
            onChange={(e) => setGrund(e.target.value)}
          />
          <p className="text-xs text-label-secondary">
            Die Begründung wird in den Projektnotizen mit der Materialanforderung verknüpft.
          </p>
        </div>
      </div>
    </Dialog>
  );
}
